import React, { Component, Suspense, lazy, Fragment } from 'react';
import { IAppProps } from './models/IAppProps';
import { IAppState } from './models/IAppState';
import { inject, observer } from 'mobx-react';
import Wrapper from './styledComponents/Wrapper';
import DocumentDownloadListWrapper from './styledComponents/DocumentDownloadListWrapper';
import NoDocumentsText from './styledComponents/NoDocumentsText';
import DebugInfoAction from '../shared/DebugInfoAction';
import NoDataPlaceholder from '@kurtosys/ksys-app-components/dist/components/base/NoDataPlaceholder';
import Disclaimer from '@kurtosys/ksys-app-components/dist/components/overview/Disclaimer';
import { InjectedStyledComponent } from '../shared/InjectedStyledComponent';
import Loading from '../shared/Loading';
import SkeletonLoader from '../shared/SkeletonLoader';
import Application from '@kurtosys/ksys-app-components/dist/components/base/Application';
import Card from '@kurtosys/ksys-app-components/dist/components/base/Card';
import Disclaimers from '@kurtosys/ksys-app-components/dist/components/overview/Disclaimers';
import DocumentDownloadList from '@kurtosys/ksys-app-components/dist/components/file/DocumentDownloadList';
import Heading from '@kurtosys/ksys-app-components/dist/components/base/Heading';
import AllDocumentDownload from './styledComponents/AllDocumentDownload';
import SelectedDocumentDownload from './styledComponents/SelectedDocumentDownload';
import { computed } from 'mobx';

@inject('appStore', 'queryStore')
@observer
export class App extends Component<IAppProps, IAppState> {
	static configurationKey: 'app' = 'app';
	static styleKey: 'app' = 'app';
	constructor(props: IAppProps) {
		super(props);
	}
	getKey(heading?: string | string[]) {
		if (Array.isArray(heading)) {
			return heading.join('_');
		}
		return heading;
	}

	@computed
	get showSelectedDocumentDownload() {
		const { appStore } = this.props;
		if (!appStore) {
			return false;
		}
		const {
			downloadSelectedLabel,
			hasAllowSelection,
			hasSelectedDocuments,
			showDownloadSelectedWhenEmpty,
		} = appStore;
		return hasAllowSelection && (showDownloadSelectedWhenEmpty || hasSelectedDocuments) && downloadSelectedLabel;
	}

	@computed
	get showAllDocumentDownload() {
		const { appStore } = this.props;
		if (!appStore) {
			return false;
		}
		const {
			hasDocuments,
			showDownloadAll,
			downloadAllLabel,
		} = appStore;
		return hasDocuments && showDownloadAll && downloadAllLabel;
	}

	render() {
		const { className, appStore, selector, queryStore } = this.props;
		if (!appStore || !queryStore) {
			return null;
		}
		const { isBootstrapped, skeletonLoader, skeletonLoaders } = appStore;
		if (!isBootstrapped) {
			if (skeletonLoader) {
				return <SkeletonLoader config={ skeletonLoader } variables={ skeletonLoaders && skeletonLoaders.variables } />;
			}
			return <Loading />;
		}
		const {
			theme,
			show,
			showPlaceholder,
			noDataPlaceholder: noDataPlaceholderText,
			noDataPlaceholderDisclaimer,
			libraryComponentsConfiguration,
			entitiesId,
			appCardProps,
			disclaimersProps,
			getTranslateFunction,
			entities,
			culture,
			assets,
			assetCacheOptions,
			assetRegisters,
			getAccessibilityTextFunction,
			documentListStores,
			downloadAllLabel,
			downloadSelectedLabel,
			downloadStore,
			documentIds,
			selectedDocumentIds,
			showDocumentDownloadLists } = appStore;

		if (!show) {
			return null;
		}

		const noDataPlaceholder =
			(noDataPlaceholderDisclaimer && <Disclaimer { ...noDataPlaceholderDisclaimer } />) ||
			(noDataPlaceholderText && <NoDataPlaceholder>{ noDataPlaceholderText }</NoDataPlaceholder>);

		const translate = getTranslateFunction();
		const accessibilityText = getAccessibilityTextFunction();
		const multilist = documentListStores.length > 1 ? 'multi-document-list' : '';
		return (
			<Application
				theme={ theme }
				configuration={ libraryComponentsConfiguration }
				translate={ translate }
				culture={ culture }
				assets={ assets }
				assetCacheOptions={ assetCacheOptions }
				assetRegisters={ assetRegisters }
				accessibilityText={ accessibilityText }
				analyticsHelper={ appStore.analyticsHelper }
			>
				<DebugInfoAction appStore={ appStore } />
				<Wrapper className={ className }>
					<Card { ...appCardProps }>
						{ showPlaceholder &&
							<NoDocumentsText dangerouslySetInnerHTML={ { __html: noDataPlaceholder } } /> }
						{ !showPlaceholder && (
							<Fragment>
								{ showDocumentDownloadLists && !entitiesId && (
									<DocumentDownloadListWrapper>
										{ (documentListStores || []).map(documentListStore => (
											<DocumentDownloadList
												className={ multilist }
												key={ this.getKey(documentListStore.props.heading) }
												{ ...documentListStore.props }></DocumentDownloadList>
										)) }
									</DocumentDownloadListWrapper>
								) }
								{ showDocumentDownloadLists && entitiesId && (
									<>
										{
											entities.map((entityStore) => {
												const headingProps = entityStore.headingProps;
												const documentListStores = entityStore.stores.documentLists;
												return (
													<>
														{ headingProps && <Heading { ...headingProps } /> }
														<DocumentDownloadListWrapper>
															{ (documentListStores || []).map(documentListStore => (
																<DocumentDownloadList
																	className={ multilist }
																	key={ this.getKey(documentListStore.props.heading) }
																	{ ...documentListStore.props }></DocumentDownloadList>
															)) }
														</DocumentDownloadListWrapper>
													</>
												);
											})
										}
									</>
								) }
								{ this.showAllDocumentDownload &&
									<AllDocumentDownload
										onDownloadClick={ downloadStore.onDownloadAll }
										label={ downloadAllLabel }
										documentIds={ documentIds } /> }
								{ this.showSelectedDocumentDownload &&
									<SelectedDocumentDownload
										onDownloadClick={ downloadStore.onDownloadAll }
										label={ downloadSelectedLabel }
										documentIds={ selectedDocumentIds } /> }
							</Fragment>
						) }
						{ disclaimersProps && <Disclaimers { ...disclaimersProps } /> }
					</Card>
				</Wrapper>
			</Application>
		);
	}
}

export default InjectedStyledComponent(App, 'app');
